import { memo, useState } from "react";
import { Check } from "@phosphor-icons/react";

interface PhotoThumbnailProps {
  image: any;
  onClick: () => void;
  isSelected?: boolean;
  onSelect?: (e: React.MouseEvent) => void;
  selectionMode?: boolean;
}

const PhotoThumbnail = memo(({ image, onClick, isSelected, onSelect, selectionMode }: PhotoThumbnailProps) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  // Prefer thumbnail (small, fast) for grid display; fall back to full URL
  const imageSrc = image.thumbnailUrl || image.imageUrl || null;
  if (!imageSrc) return null;

  // Prefer unit ID (more specific) over building ID
  const locationId = image.unit?.id || image.unitId || image.building?.id || image.buildingId || '';
  const displayDate = image.photoTakenDate || image.documentDate || image.createdAt;

  return (
    <div
      className={`group relative cursor-pointer overflow-hidden rounded-sm aspect-square ${
        isSelected ? "ring-2 ring-moss-primary" : ""
      }`}
      style={{ contentVisibility: 'auto', containIntrinsicSize: '150px' }}
      onClick={onClick}
    >
      {!loaded && !error && (
        <div className="absolute inset-0 bg-neutral-200 animate-pulse" />
      )}
      <img
        src={imageSrc}
        alt=""
        className={`h-full w-full object-cover ${loaded ? "" : "opacity-0"}`}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => { setError(true); setLoaded(true); }}
      />
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-100 text-neutral-secondary-mid text-[10px]">
          Fout
        </div>
      )}

      {/* Selection checkbox — always available on hover so users can initiate selection */}
      {onSelect && (
        <button
          onClick={(e) => { e.stopPropagation(); onSelect(e); }}
          aria-label={isSelected ? "Deselecteer foto" : "Selecteer foto"}
          className={`absolute top-1 right-1 z-10 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-opacity ${
            isSelected
              ? "border-moss-primary bg-moss-primary text-white opacity-100"
              : selectionMode
                ? "border-white/80 bg-black/30 text-transparent opacity-100 hover:border-moss-primary hover:bg-moss-primary/40"
                : "border-white/80 bg-black/30 text-transparent opacity-0 group-hover:opacity-100 hover:border-moss-primary hover:bg-moss-primary/40"
          }`}
        >
          <Check size={12} weight="bold" />
        </button>
      )}

      {/* Bottom overlay — only rendered when loaded */}
      {loaded && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 to-transparent px-1.5 pb-1 pt-4">
          {locationId && (
            <p className="text-[10px] font-medium text-white truncate leading-tight">{locationId}</p>
          )}
          {displayDate && (
            <p className="text-[9px] text-white/80 leading-tight">
              {new Date(displayDate).toLocaleDateString('nl-BE')}
            </p>
          )}
          {(image.locationDescription || image.building?.googlePlaceLongAddress) && (
            <p className="text-[9px] text-white/70 truncate leading-tight">
              {image.locationDescription || image.building?.googlePlaceLongAddress}
            </p>
          )}
        </div>
      )}

      {/* Labels top-left — only rendered when loaded */}
      {loaded && image.label?.[0] && (
        <div className="absolute top-1 left-1 flex flex-col items-start gap-px">
          <span className="rounded-full bg-brick-primary/90 px-1.5 py-px text-[8px] font-medium text-white leading-tight">
            {image.label[0]}
          </span>
          {image.secondaryLabels
            ?.map((label: unknown) => {
              // Defensive: the DB has some rows where simple-array stored
              // serialized objects ("[object Object]"). Also handle the
              // nested {label: "..."} shape older clients sometimes sent.
              if (typeof label === "string") {
                return label === "[object Object]" ? null : label;
              }
              if (label && typeof label === "object" && "label" in (label as any)) {
                const inner = (label as any).label;
                return typeof inner === "string" ? inner : null;
              }
              return null;
            })
            .filter((l: string | null): l is string => !!l)
            .map((label: string) => (
              <span
                key={label}
                className="rounded-full bg-moss-primary/80 px-1.5 py-px text-[8px] font-medium text-white/90 leading-tight"
              >
                {label}
              </span>
            ))}
        </div>
      )}
    </div>
  );
});

PhotoThumbnail.displayName = 'PhotoThumbnail';

export default PhotoThumbnail;
